import type {
    ContextFeatureAllowedValue,
    ContextFeatureControlType,
    ContextFeatureDependencySpec,
    ContextFeatureFilterSchema,
    ContextFeatureJsonObject,
    ContextFeatureJsonValue,
    ContextFeatureManifestMetadata,
    ContextFeatureOptimizerMetadata,
    ContextFeatureProviderRuntime,
    ContextFeatureProviderSource,
    ContextFeatureProviderStatus,
    ContextFeatureSelectorSchema,
    ContextFeatureSelectorSchemaEntry,
    ContextFeatureSupportMatrix,
    ContextFeatureUiMetadata,
    ContextFeatureUiTier,
    ControlSpec,
    TradeLogicComparisonOperatorV2,
    TradeLogicEvaluationModeV2
} from '@/types/appRouter';
import type {
    TradeLogicRegistryActionTemplateDto,
    TradeLogicRegistryCapabilitySummary,
    TradeLogicRegistryConditionTemplateDto,
    TradeLogicRegistryConditionTemplateFeatureOptionDto,
    TradeLogicRegistryFeatureDto,
    TradeLogicRegistryManifestDto,
    TradeLogicRegistryProviderDto,
    TradeLogicRegistrySchemaFieldDto,
    TradeLogicRegistrySurface
} from './types';

const REGISTRY_SURFACES: TradeLogicRegistrySurface[] = [
    'backtest',
    'paper',
    'live',
    'simulation',
    'tsBacktest',
    'tsLive',
    'rustBacktest',
    'rustLive',
    'rustReplay'
];

const EVALUATION_MODES: TradeLogicEvaluationModeV2[] = ['live', 'backtest', 'paper', 'simulation'];

const EMPTY_CAPABILITIES: TradeLogicRegistryCapabilitySummary = {
    surfaces: {
        backtest: false,
        paper: false,
        live: false,
        simulation: false,
        tsBacktest: false,
        tsLive: false,
        rustBacktest: false,
        rustLive: false,
        rustReplay: false
    },
    supportedSurfaces: [],
    evaluationModes: {
        live: false,
        backtest: false,
        paper: false,
        simulation: false
    },
    supportedEvaluationModes: [],
    canBacktest: false,
    canLive: false,
    canPaper: false,
    canSimulate: false,
    canAuthor: false,
    unsupportedReason: null
};

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
}

function asString(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
}

function asNonEmptyString(value: unknown, fallback: string): string {
    const stringValue = asString(value);
    return stringValue && stringValue.trim().length > 0 ? stringValue : fallback;
}

function asNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asBoolean(value: unknown): boolean | null {
    return typeof value === 'boolean' ? value : null;
}

function asJsonObject(value: unknown): ContextFeatureJsonObject | null {
    const record = asRecord(value);
    return Object.keys(record).length > 0 ? (record as ContextFeatureJsonObject) : null;
}

function asSchema(value: unknown): ContextFeatureSelectorSchema {
    return asRecord(value) as ContextFeatureSelectorSchema;
}

function asFilterSchema(value: unknown): ContextFeatureFilterSchema {
    return asRecord(value) as ContextFeatureFilterSchema;
}

function asAllowedValues(value: unknown): ContextFeatureAllowedValue[] {
    return asArray(value).filter(
        (item): item is ContextFeatureAllowedValue => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean'
    );
}

function asStringArray(value: unknown): string[] {
    return asArray(value).filter((item): item is string => typeof item === 'string');
}

function asOperators(value: unknown): TradeLogicComparisonOperatorV2[] {
    return asStringArray(value) as TradeLogicComparisonOperatorV2[];
}

function asJsonValue(value: unknown): ContextFeatureJsonValue | null {
    if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
    if (Array.isArray(value)) return value as ContextFeatureJsonValue;
    if (typeof value === 'object') return value as ContextFeatureJsonValue;
    return null;
}

const VALID_CONTROL_TYPES: readonly ContextFeatureControlType[] = [
    'boolean',
    'select',
    'multiSelect',
    'number',
    'text',
    'levelKey',
    'json',
    'duration',
    'percent',
    'money'
];

function asControlType(value: unknown): ContextFeatureControlType | null {
    return typeof value === 'string' && (VALID_CONTROL_TYPES as readonly string[]).includes(value)
        ? (value as ContextFeatureControlType)
        : null;
}

function readControlSpec(value: unknown): ControlSpec | null {
    if (!value || typeof value !== 'object') return null;
    const record = value as Record<string, unknown>;
    const controlType = asControlType(record.controlType);
    if (!controlType) return null;

    return {
        controlType,
        label: asString(record.label) ?? '',
        required: asBoolean(record.required) ?? false,
        description: asString(record.description),
        allowedValues: Array.isArray(record.allowedValues) ? asAllowedValues(record.allowedValues) : null,
        defaultValue: asJsonValue(record.defaultValue),
        examples: Array.isArray(record.examples)
            ? (record.examples as unknown[]).map(asJsonValue).filter((item): item is ContextFeatureJsonValue => item !== null)
            : null,
        min: asNumber(record.min),
        max: asNumber(record.max),
        step: asNumber(record.step),
        placeholder: asString(record.placeholder)
    };
}

function readControlSpecMap(value: unknown): Record<string, ControlSpec> {
    const record = asRecord(value);
    const result: Record<string, ControlSpec> = {};

    for (const [key, rawSpec] of Object.entries(record)) {
        const spec = readControlSpec(rawSpec);
        if (spec) result[key] = spec;
    }

    return result;
}

function normalizeSchemaFields(
    schema: ContextFeatureSelectorSchema | ContextFeatureFilterSchema,
    controlsByKey: Record<string, ControlSpec> = {}
): TradeLogicRegistrySchemaFieldDto[] {
    return Object.entries(schema)
        .map(([key, rawEntry]) => {
            const entry = asRecord(rawEntry);
            const control = controlsByKey[key] ?? null;
            const label = control?.label || asString(entry.label) || key;
            const allowedValues = asAllowedValues(entry.allowedValues);

            const examples = asArray(entry.examples)
                .map(asJsonValue)
                .filter((item): item is ContextFeatureJsonValue => item !== null);

            return {
                key,
                type: asString(entry.type),
                operators: asOperators(entry.operators),
                required: control?.required ?? asBoolean(entry.required) ?? false,
                label,
                description: control?.description ?? asString(entry.description),
                allowedValues: control?.allowedValues ?? allowedValues,
                defaultValue: control?.defaultValue ?? asJsonValue(entry.defaultValue),
                examples: control?.examples ?? examples,
                controlType: control?.controlType ?? null,
                min: control?.min ?? null,
                max: control?.max ?? null,
                step: control?.step ?? null,
                placeholder: control?.placeholder ?? null
            };
        })
        .sort((left, right) => left.label.localeCompare(right.label));
}

function normalizeCapabilities(rawSupport: unknown): TradeLogicRegistryCapabilitySummary {
    const support = asRecord(rawSupport);
    if (Object.keys(support).length === 0) return EMPTY_CAPABILITIES;

    const modes = asRecord(support.modes);

    const surfaces: Record<TradeLogicRegistrySurface, boolean> = {
        backtest: asBoolean(support.tsBacktest) === true || asBoolean(support.rustBacktest) === true,
        paper: asBoolean(asRecord(modes.paper).supported) === true,
        live: asBoolean(support.tsLive) === true || asBoolean(support.rustLive) === true,
        simulation: asBoolean(asRecord(modes.simulation).supported) === true,
        tsBacktest: asBoolean(support.tsBacktest) === true,
        tsLive: asBoolean(support.tsLive) === true,
        rustBacktest: asBoolean(support.rustBacktest) === true,
        rustLive: asBoolean(support.rustLive) === true,
        rustReplay: asBoolean(support.rustReplay) === true
    };

    const evaluationModes: Record<TradeLogicEvaluationModeV2, boolean> = {
        live: asBoolean(asRecord(modes.live).supported) ?? surfaces.live,
        backtest: asBoolean(asRecord(modes.backtest).supported) ?? surfaces.backtest,
        paper: asBoolean(asRecord(modes.paper).supported) ?? surfaces.paper,
        simulation: asBoolean(asRecord(modes.simulation).supported) ?? surfaces.simulation
    };

    const supportedSurfaces = REGISTRY_SURFACES.filter((surface) => surfaces[surface]);
    const supportedEvaluationModes = EVALUATION_MODES.filter((mode) => evaluationModes[mode]);

    return {
        surfaces,
        supportedSurfaces,
        evaluationModes,
        supportedEvaluationModes,
        canBacktest: evaluationModes.backtest,
        canLive: evaluationModes.live,
        canPaper: evaluationModes.paper,
        canSimulate: evaluationModes.simulation,
        canAuthor: supportedSurfaces.length > 0 || supportedEvaluationModes.length > 0,
        unsupportedReason: asString(support.unsupportedReason)
    };
}

export function normalizeRegistryProvider(rawProvider: unknown): TradeLogicRegistryProviderDto | null {
    const provider = asRecord(rawProvider);
    const providerId = asString(provider.providerId);
    if (!providerId) return null;

    const status = asString(provider.status) as ContextFeatureProviderStatus | null;

    return {
        providerId,
        label: asNonEmptyString(provider.label, providerId),
        runtime: asString(provider.runtime) as ContextFeatureProviderRuntime | null,
        status,
        source: asString(provider.source) as ContextFeatureProviderSource | null,
        materialization: asString(provider.materialization),
        version: asNumber(provider.version),
        semanticFingerprint: asString(provider.semanticFingerprint),
        deterministic: asBoolean(provider.deterministic),
        owner: asString(provider.owner),
        description: asString(provider.description),
        metadata: asJsonObject(provider.metadata),
        isActive: status === 'active' || status === 'compatibility',
        isDeprecated: status === 'deprecated' || status === 'retired'
    };
}

export function normalizeRegistryProviders(rawProviders: unknown): TradeLogicRegistryProviderDto[] {
    return asArray(rawProviders)
        .map(normalizeRegistryProvider)
        .filter((provider): provider is TradeLogicRegistryProviderDto => provider !== null)
        .sort((left, right) => left.label.localeCompare(right.label));
}

const VALID_TIERS: readonly ContextFeatureUiTier[] = ['primary', 'secondary', 'advanced'];

function asTier(value: unknown): ContextFeatureUiTier | null {
    return typeof value === 'string' && (VALID_TIERS as readonly string[]).includes(value) ? (value as ContextFeatureUiTier) : null;
}

function familyTopLevelLabel(family: string): string {
    const top = family.split('.')[0] ?? family;
    if (!top) return family;
    const words = top.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
    return words.charAt(0).toUpperCase() + words.slice(1);
}

function readGroupPath(ui: ContextFeatureUiMetadata | null, family: string, groupLabel: string): string[] {
    if (ui && Array.isArray(ui.groupPath)) {
        const path = ui.groupPath.filter((item): item is string => typeof item === 'string' && item.length > 0);
        if (path.length > 0) return path;
    }

    const top = familyTopLevelLabel(family);
    return top === groupLabel ? [groupLabel] : [top, groupLabel];
}

export function normalizeRegistryFeature(
    rawFeature: unknown,
    providersById: Record<string, TradeLogicRegistryProviderDto> = {}
): TradeLogicRegistryFeatureDto | null {
    const feature = asRecord(rawFeature);
    const featureKey = asString(feature.featureKey);
    if (!featureKey) return null;

    const providerId = asNonEmptyString(feature.providerId, '');
    const rawProvider = normalizeRegistryProvider(feature.provider);
    const provider = rawProvider ?? providersById[providerId] ?? null;
    const selectors = asSchema(feature.selectors);
    const filters = asFilterSchema(feature.filters);
    const label = asNonEmptyString(feature.label, featureKey);
    const family = asNonEmptyString(feature.family, 'Uncategorized');
    const description = asString(feature.description);
    const ui = (asJsonObject(feature.ui) as ContextFeatureUiMetadata | null) ?? null;
    const uiRecord = asRecord(feature.ui);
    const optimizer = (asJsonObject(feature.optimizer) as ContextFeatureOptimizerMetadata | null) ?? null;
    const support = Object.keys(asRecord(feature.support)).length > 0 ? (feature.support as ContextFeatureSupportMatrix) : null;
    const capabilities = normalizeCapabilities(support);
    const legacyGroup = asString(ui?.group) ?? asString(optimizer?.scorecardCategory) ?? family;
    const displayLabel = asString(uiRecord.displayLabel) ?? label;
    const groupLabel = asString(uiRecord.groupLabel) ?? legacyGroup;
    const groupPath = readGroupPath(ui, family, groupLabel);
    const tier = asTier(uiRecord.tier);
    const valueControl = readControlSpec(uiRecord.valueControl);
    const selectorControls = readControlSpecMap(uiRecord.selectorControls);
    const filterControls = readControlSpecMap(uiRecord.filterControls);
    const providerLabel = provider?.label ?? (providerId || null);

    return {
        featureKey,
        label,
        displayLabel,
        description,
        family,
        providerId,
        provider,
        providerLabel,
        valueShape: asString(feature.valueShape) ?? null,
        valueKind: asString(feature.valueKind),
        cardinality: asString(feature.cardinality),
        valueEnum: asAllowedValues(feature.valueEnum),
        allowedOperators: asOperators(feature.allowedOperators),
        selectors,
        selectorFields: normalizeSchemaFields(selectors, selectorControls),
        filters,
        filterFields: normalizeSchemaFields(filters, filterControls),
        dependencies:
            Object.keys(asRecord(feature.dependencies)).length > 0 ? (feature.dependencies as ContextFeatureDependencySpec) : null,
        support,
        capabilities,
        version: asNumber(feature.version),
        semanticFingerprint: asString(feature.semanticFingerprint),
        defaultDuration: asString(feature.defaultDuration),
        ui,
        optimizer,
        metadata: asJsonObject(feature.metadata),
        isDiscoverable: asBoolean(optimizer?.discoverable) ?? true,
        sortKey: `${groupLabel} ${displayLabel} ${featureKey}`.toLowerCase(),
        searchText: [
            displayLabel,
            label,
            featureKey,
            description,
            family,
            providerId,
            providerLabel,
            groupLabel,
            ...groupPath,
            ...(ui?.tags ?? [])
        ]
            .filter((part): part is string => typeof part === 'string' && part.length > 0)
            .join(' ')
            .toLowerCase(),
        groupLabel,
        groupPath,
        tier,
        valueControl
    };
}

export function normalizeRegistryFeatures(
    rawFeatures: unknown,
    providersById: Record<string, TradeLogicRegistryProviderDto> = {}
): TradeLogicRegistryFeatureDto[] {
    return asArray(rawFeatures)
        .map((feature) => normalizeRegistryFeature(feature, providersById))
        .filter((feature): feature is TradeLogicRegistryFeatureDto => feature !== null)
        .sort((left, right) => left.sortKey.localeCompare(right.sortKey));
}

export function normalizeActionTemplate(rawTemplate: unknown): TradeLogicRegistryActionTemplateDto | null {
    const template = asRecord(rawTemplate);
    const templateKey = asString(template.templateKey);
    if (!templateKey) return null;

    const parameters = asSchema(template.parameters);
    const label = asNonEmptyString(template.label, templateKey);
    const description = asString(template.description);
    const actionKind = asString(template.actionKind);
    const ui = (asJsonObject(template.ui) as ContextFeatureUiMetadata | null) ?? null;
    const uiRecord = asRecord(template.ui);
    const displayLabel = asString(uiRecord.displayLabel) ?? label;
    const legacyGroup = asString(ui?.group) ?? '';
    const groupLabel = asString(uiRecord.groupLabel) ?? legacyGroup;
    const groupPath = readGroupPath(ui, actionKind ?? legacyGroup, groupLabel || legacyGroup);
    const tier = asTier(uiRecord.tier);
    const selectorControls = readControlSpecMap(uiRecord.selectorControls);

    return {
        templateKey,
        label,
        displayLabel,
        description,
        actionKind,
        documentKinds: asStringArray(template.documentKinds),
        parameters,
        parameterFields: normalizeSchemaFields(parameters, selectorControls),
        defaults: asJsonObject(template.defaults),
        version: asNumber(template.version),
        semanticFingerprint: asString(template.semanticFingerprint),
        ui,
        metadata: asJsonObject(template.metadata),
        sortKey: `${groupLabel} ${displayLabel} ${templateKey}`.toLowerCase(),
        searchText: [displayLabel, label, templateKey, description, actionKind, groupLabel, ...groupPath, ...(ui?.tags ?? [])]
            .filter((part): part is string => typeof part === 'string' && part.length > 0)
            .join(' ')
            .toLowerCase(),
        groupLabel,
        groupPath,
        tier
    };
}

export function normalizeActionTemplates(rawTemplates: unknown): TradeLogicRegistryActionTemplateDto[] {
    return asArray(rawTemplates)
        .map(normalizeActionTemplate)
        .filter((template): template is TradeLogicRegistryActionTemplateDto => template !== null)
        .sort((left, right) => left.sortKey.localeCompare(right.sortKey));
}

function normalizeConditionFeatureOption(rawOption: unknown): TradeLogicRegistryConditionTemplateFeatureOptionDto | null {
    const option = asRecord(rawOption);
    const optionKey = asString(option.optionKey);
    if (!optionKey) return null;

    const featureRefRecord = asRecord(option.featureRef);
    const featureKey = asString(featureRefRecord.featureKey);

    return {
        optionKey,
        label: asNonEmptyString(option.label, optionKey),
        description: asString(option.description),
        featureRef: Object.keys(featureRefRecord).length > 0 ? featureRefRecord : null,
        featureKey,
        defaultOperator: asString(option.defaultOperator) as TradeLogicComparisonOperatorV2 | null,
        defaultValue: asJsonValue(option.defaultValue)
    };
}

export function normalizeConditionTemplate(rawTemplate: unknown): TradeLogicRegistryConditionTemplateDto | null {
    const template = asRecord(rawTemplate);
    const templateKey = asString(template.templateKey);
    if (!templateKey) return null;

    const parameters = asSchema(template.parameters);

    const contextFeatureOptions = asArray(template.contextFeatureOptions)
        .map(normalizeConditionFeatureOption)
        .filter((option): option is TradeLogicRegistryConditionTemplateFeatureOptionDto => option !== null);

    const featureKeys = Array.from(
        new Set(contextFeatureOptions.map((option) => option.featureKey).filter((featureKey): featureKey is string => featureKey !== null))
    ).sort((left, right) => left.localeCompare(right));

    const label = asNonEmptyString(template.label, templateKey);
    const description = asString(template.description);
    const requirementType = asString(template.requirementType);
    const ui = (asJsonObject(template.ui) as ContextFeatureUiMetadata | null) ?? null;
    const uiRecord = asRecord(template.ui);
    const rawDisplayLabel = asString(uiRecord.displayLabel);
    const legacyGroup = asString(ui?.group) ?? '';
    const groupLabel = asString(uiRecord.groupLabel) ?? legacyGroup;

    const displayLabel =
        rawDisplayLabel && rawDisplayLabel === groupLabel && label !== rawDisplayLabel ? label : (rawDisplayLabel ?? label);

    const groupPath = readGroupPath(ui, requirementType ?? legacyGroup, groupLabel || legacyGroup);
    const tier = asTier(uiRecord.tier);
    const selectorControls = readControlSpecMap(uiRecord.selectorControls);

    return {
        templateKey,
        label,
        displayLabel,
        description,
        requirementType,
        documentKinds: asStringArray(template.documentKinds),
        contextFeatureOptions,
        featureKeys,
        allowedOperators: asOperators(template.allowedOperators),
        valueSchema:
            Object.keys(asRecord(template.valueSchema)).length > 0 ? (template.valueSchema as ContextFeatureSelectorSchemaEntry) : null,
        parameters,
        parameterFields: normalizeSchemaFields(parameters, selectorControls),
        version: asNumber(template.version),
        semanticFingerprint: asString(template.semanticFingerprint),
        ui,
        metadata: asJsonObject(template.metadata),
        sortKey: `${groupLabel} ${displayLabel} ${templateKey}`.toLowerCase(),
        searchText: [
            displayLabel,
            label,
            templateKey,
            description,
            requirementType,
            groupLabel,
            ...groupPath,
            ...featureKeys,
            ...(ui?.tags ?? [])
        ]
            .filter((part): part is string => typeof part === 'string' && part.length > 0)
            .join(' ')
            .toLowerCase(),
        groupLabel,
        groupPath,
        tier
    };
}

export function normalizeConditionTemplates(rawTemplates: unknown): TradeLogicRegistryConditionTemplateDto[] {
    return asArray(rawTemplates)
        .map(normalizeConditionTemplate)
        .filter((template): template is TradeLogicRegistryConditionTemplateDto => template !== null)
        .sort((left, right) => left.sortKey.localeCompare(right.sortKey));
}

function indexByKey<T>(items: T[], getKey: (item: T) => string): Record<string, T> {
    return Object.fromEntries(items.map((item) => [getKey(item), item]));
}

export function normalizeRegistryManifest(rawManifest: unknown): TradeLogicRegistryManifestDto {
    const manifest = asRecord(rawManifest);
    const providers = normalizeRegistryProviders(manifest.providers);
    const providersById = indexByKey(providers, (provider) => provider.providerId);
    const features = normalizeRegistryFeatures(manifest.features, providersById);
    const actionTemplates = normalizeActionTemplates(manifest.actionTemplates);
    const conditionTemplates = normalizeConditionTemplates(manifest.conditionTemplates);

    return {
        schemaVersion: asNumber(manifest.schemaVersion),
        manifestId: asString(manifest.manifestId),
        manifestVersion: asNumber(manifest.manifestVersion),
        semanticFingerprint: asString(manifest.semanticFingerprint),
        generatedAt: asString(manifest.generatedAt),
        providers,
        providersById,
        features,
        featuresByKey: indexByKey(features, (feature) => feature.featureKey),
        actionTemplates,
        actionTemplatesByKey: indexByKey(actionTemplates, (template) => template.templateKey),
        conditionTemplates,
        conditionTemplatesByKey: indexByKey(conditionTemplates, (template) => template.templateKey),
        metadata: Object.keys(asRecord(manifest.metadata)).length > 0 ? (manifest.metadata as ContextFeatureManifestMetadata) : null
    };
}
